// Global Instructions Rule Applied!
//
// Deal form Execution tab — Round Securities card (owner restructure
// 2026-08-16; inline-fields layout per owner feedback the same day).
//
// The round's securities, categorised primary / secondary / converting: a
// round has at most one primary and any number of secondary (existing paper
// purchased) or converting (SAFEs / notes rolling into this round).
//
// The card reads like part of the deal record: each security's own fields are
// laid out as label/value rows and edited IN PLACE on this form — the same
// field set, cfg-driven visibility and percent conventions as the Security
// full form. Each block is read-only until its Edit button (owner
// 2026-08-16), then Save / Cancel; clicking away from an open block saves it.
// Save writes the terms to the SECURITY (at__update_security, partial: only
// the fields changed since Edit was clicked are sent, so a draft seeded from
// a stale context can never overwrite edits made elsewhere) and the category
// to the round_instruments link row, which stores nothing but the category
// and the security id. Change (re-point the row at another security) and
// Delete live in the block's ellipsis menu.
//
// A deal execution is usually the first moment a security exists, so the card
// creates one in place (the shared AddSecurityModal) as well as linking one
// the company already has.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Dropdown, Input, Modal, Select, Tag, Tooltip } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import InputNumber from '../../../core/components/InputNumber.jsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { decimalToPercent, percentToDecimal } from '../../../core/utils/inputNumberFormat.ts'
import AddSecurityModal from '../../asset_transactions/components/AddSecurityModal.tsx'
import { saveSecurity, SaveSecurityPayload } from '../../securities/services/securitiesService.ts'
import {
  DealExecutionContext,
  DealExecutionRoundInstrument
} from '../../../state_manager/data_models/deal_execution_board_model.ts'

type Kind = 'primary' | 'secondary' | 'converting'

const KIND_OPTIONS: { value: Kind; label: string }[] = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'converting', label: 'Converting' }
]
const KIND_COLORS: Record<string, string> = {
  primary: 'blue',
  secondary: 'purple',
  converting: 'gold'
}

const DATE_FORMAT = 'MM/DD/YYYY'

/** Label + control row — the Execution tab's house field row (RoundCard). */
function Row({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className='flex items-center gap-3 py-1'>
      <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
      <div className='flex-1 min-w-0'>{children}</div>
    </div>
  )
}

/** The editable snapshot of one security, as the save RPC wants it. */
interface SecurityFields {
  name: string
  type_id: number | null
  valuation_type_id: number | null
  currency_id: string | null
  issue_price: number | null
  nominal_value: number | null
  // percent fields are held as PERCENTAGES here and saved as decimals
  interest_rate: number | null
  interest_rate_type_id: number | null
  compounding_frequency_id: number | null
  day_count_convention_id: number | null
  maturity_date: string | null
  maturity_months: number | null
  valuation_cap_type_id: number | null
  valuation_cap: number | null
  valuation_floor: number | null
  conversion_discount: number | null
  strike_price: number | null
  fd_common_equivalent: number | null
}

function fieldsFromInstrument(i: DealExecutionRoundInstrument): SecurityFields {
  return {
    name: i.securities__name || '',
    type_id: i.cfg_security_types__id ?? null,
    valuation_type_id: i.cfg_asset_valuation_types__id ?? null,
    currency_id: i.currencies__id ?? null,
    issue_price: i.securities__issue_price ?? null,
    nominal_value: i.securities__nominal_value ?? null,
    interest_rate: decimalToPercent(i.securities__interest_rate),
    interest_rate_type_id: i.cfg_interest_rate_types__id ?? null,
    compounding_frequency_id: i.cfg_compounding_frequencies__id ?? null,
    day_count_convention_id: i.cfg_day_count_conventions__id ?? null,
    maturity_date: i.securities__maturity_date ?? null,
    maturity_months: i.securities__maturity_months ?? null,
    valuation_cap_type_id: i.cfg_valuation_cap_types__id ?? null,
    valuation_cap: i.securities__valuation_cap ?? null,
    valuation_floor: i.securities__valuation_floor ?? null,
    conversion_discount: decimalToPercent(i.securities__conversion_discount),
    strike_price: i.securities__strike_price ?? null,
    fd_common_equivalent: i.securities__fd_common_equivalent ?? null
  }
}

interface RoundSecuritiesCardProps {
  context: DealExecutionContext | null
  /** The issuing company — new securities are issued by it */
  companyId: string | null | undefined
  companyName: string | null | undefined
  onSaveInstrument: (instrumentId: string | null, fields: Record<string, unknown>) => Promise<boolean>
  onRemoveInstrument: (instrumentId: string) => Promise<boolean>
  /** Re-read the context after a security's own fields change */
  onSecuritySaved: () => Promise<void> | void
}

/**
 * One security block: category + linked security in the header, the security's
 * own terms as label/value rows beneath.
 *
 * Read-only until Edit (owner 2026-08-16) — the fields are the security's, not
 * the round's, so they are not live-edited by accident. Edit unlocks the whole
 * block; Save writes it (the save RPC is a full-row upsert, so one call sends
 * the snapshot); Cancel reverts. Clicking away from an open block saves it —
 * the default is to keep what was typed, not to discard it.
 */
function SecurityBlock({
  instrument,
  kindOptions,
  onKindChange,
  onRelink,
  onRemove,
  onSecuritySaved,
  securityOptions,
  openSecurity
}: {
  instrument: DealExecutionRoundInstrument
  kindOptions: { value: Kind; label: string; disabled?: boolean }[]
  onKindChange: (kind: Kind) => Promise<void>
  onRelink: (securityId: string | null) => Promise<void>
  onRemove: () => Promise<void>
  onSecuritySaved: () => Promise<void> | void
  securityOptions: { value: string; label: string; typeLabel: string }[]
  openSecurity: (securityId: string, name: string) => void
}): React.ReactElement {
  const securityTypes = useSelector((state: any) => state.globalConfigs?.security_types ?? [])
  const valuationTypes = useSelector((state: any) => state.globalConfigs?.asset_valuation_types ?? [])
  const currencies = useSelector((state: any) => state.globalConfigs?.currencies ?? [])
  const valuationCapTypes = useSelector((state: any) => state.globalConfigs?.valuation_cap_types ?? [])
  const interestRateTypes = useSelector((state: any) => state.globalConfigs?.interest_rate_types ?? [])
  const compoundingFrequencies = useSelector(
    (state: any) => state.globalConfigs?.compounding_frequencies ?? []
  )
  const dayCountConventions = useSelector((state: any) => state.globalConfigs?.day_count_conventions ?? [])

  const [fields, setFields] = useState<SecurityFields>(() => fieldsFromInstrument(instrument))
  const [kind, setKind] = useState<Kind>(instrument.round_instruments__kind as Kind)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [relinking, setRelinking] = useState(false)
  const [modal, modalContextHolder] = Modal.useModal()
  const blockRef = useRef<HTMLDivElement | null>(null)
  // Click-away runs from a document listener, which closes over stale state
  // unless the save path is held in a ref.
  const saveRef = useRef<() => Promise<void>>(async () => {})
  // Snapshot of the draft at the moment Edit was clicked — save() sends only
  // the fields that differ from it.
  const baselineRef = useRef<SecurityFields | null>(null)

  const securityId = instrument.securities__id

  // The context refetch after every save is the source of truth — re-seed the
  // draft whenever the row changes underneath (including a re-link), except
  // while the operator is mid-edit. Each refetch rebuilds the instrument
  // object, so identity is the change signal.
  useEffect(() => {
    if (editing) return
    setFields(fieldsFromInstrument(instrument))
    setKind(instrument.round_instruments__kind as Kind)
  }, [editing, instrument])

  const setDraft = (patch: Partial<SecurityFields>): void => setFields({ ...fields, ...patch })

  // cfg-driven visibility — identical rules to the Security full form and the
  // "+ New security" modal: flags on cfg.security_types decide which terms
  // apply; a type with no flags (Loan, Token, …) keeps the full set.
  const selType = securityTypes.find((t: any) => t.id === fields.type_id)
  const anyFlag = Boolean(
    selType &&
      (selType.is_convertible ||
        selType.uses_interest ||
        selType.uses_nominal ||
        selType.uses_strike ||
        selType.uses_issue_price ||
        selType.uses_maturity)
  )
  const showAll = Boolean(selType) && !anyFlag
  const showConvertible = Boolean(selType?.is_convertible)
  const usesInterest = Boolean(selType?.uses_interest)
  const usesMaturity = Boolean(selType?.uses_maturity)
  const capType = valuationCapTypes.find((v: any) => v.id === fields.valuation_cap_type_id)
  const irType = interestRateTypes.find((r: any) => r.id === fields.interest_rate_type_id)
  const ccySymbol = currencies.find((c: any) => c.id === fields.currency_id)?.symbol || '$'

  // Applicability per the cfg flags…
  const capApplies = showAll || (showConvertible && Boolean(capType?.has_cap))
  const floorApplies = showAll || (showConvertible && Boolean(capType?.has_floor))
  const compoundingApplies = showAll || (usesInterest && Boolean(irType?.compounds))
  const issuePriceApplies = showAll || Boolean(selType?.uses_issue_price)
  const nominalApplies = showAll || Boolean(selType?.uses_nominal)
  const strikeApplies = showAll || Boolean(selType?.uses_strike)
  const interestApplies = showAll || usesInterest
  const maturityApplies = showAll || usesMaturity
  const maturityMonthsApplies = showAll || (usesMaturity && showConvertible)
  const convertibleApplies = showAll || showConvertible

  // …but a term the security ALREADY carries is always shown, even when the
  // flags say it shouldn't apply (a SAFE with a cap but no cap type recorded
  // is real data — hiding it would make the card lie about the security).
  const has = (v: number | string | null | undefined): boolean => v != null && v !== ''
  const showIssuePrice = issuePriceApplies || has(fields.issue_price)
  const showNominal = nominalApplies || has(fields.nominal_value)
  const showCapType = convertibleApplies || has(fields.valuation_cap_type_id)
  const showCap = capApplies || has(fields.valuation_cap)
  const showFloor = floorApplies || has(fields.valuation_floor)
  const showDiscount = convertibleApplies || has(fields.conversion_discount)
  const showInterest = interestApplies || has(fields.interest_rate) || has(fields.interest_rate_type_id)
  const showCompounding = compoundingApplies || has(fields.compounding_frequency_id)
  const showMaturity = maturityApplies || has(fields.maturity_date)
  const showMaturityMonths = maturityMonthsApplies || has(fields.maturity_months)
  const showStrike = strikeApplies || has(fields.strike_price)

  /**
   * Write the block: the category to the link row, the terms to the security.
   *
   * The payload is a DIFF against the snapshot taken when Edit was clicked —
   * at__update_security keeps every absent key's stored value, so a draft
   * seeded from a stale context (the tab is never unmounted) cannot overwrite
   * edits made elsewhere, e.g. on the Security full form. A security-TYPE
   * change is what clears inapplicable terms — switching a SAFE to Common
   * Stock must not carry a stale cap. A no-op edit (click-away with nothing
   * changed) writes nothing.
   */
  const save = async (): Promise<void> => {
    if (saving) return
    const baseline = baselineRef.current ?? fieldsFromInstrument(instrument)
    const typeChanged = fields.type_id !== baseline.type_id
    const kindChanged = kind !== instrument.round_instruments__kind
    if (securityId && (!fields.name.trim() || !fields.type_id || !fields.valuation_type_id || !fields.currency_id)) {
      showErrorToast('Name, security type, valuation type and currency are required')
      return
    }
    setSaving(true)
    try {
      if (kindChanged) await onKindChange(kind)
      let securityWritten = false
      if (securityId) {
        // The new type's flags decide what survives a type change.
        const nextType = securityTypes.find((t: any) => t.id === fields.type_id)
        const nextAnyFlag = Boolean(
          nextType &&
            (nextType.is_convertible ||
              nextType.uses_interest ||
              nextType.uses_nominal ||
              nextType.uses_strike ||
              nextType.uses_issue_price ||
              nextType.uses_maturity)
        )
        const all = Boolean(nextType) && !nextAnyFlag
        const changed = (k: keyof SecurityFields): boolean => fields[k] !== baseline[k]
        const payload: Record<string, unknown> = {}
        // Identity fields go in only when the operator changed them.
        const put = (key: string, k: keyof SecurityFields, value: unknown): void => {
          if (changed(k)) payload[key] = value
        }
        // Terms additionally get nulled when a type change strips them.
        const term = (key: string, applies: boolean, k: keyof SecurityFields, value: unknown): void => {
          if (typeChanged && !applies) payload[key] = null
          else if (changed(k)) payload[key] = value
        }
        put('name', 'name', fields.name.trim())
        put('type_id', 'type_id', fields.type_id)
        put('valuation_type_id', 'valuation_type_id', fields.valuation_type_id)
        put('currency_id', 'currency_id', fields.currency_id)
        term('issue_price', all || Boolean(nextType?.uses_issue_price), 'issue_price', fields.issue_price)
        term('nominal_value', all || Boolean(nextType?.uses_nominal), 'nominal_value', fields.nominal_value)
        term(
          'interest_rate',
          all || Boolean(nextType?.uses_interest),
          'interest_rate',
          percentToDecimal(fields.interest_rate)
        )
        term(
          'interest_rate_type_id',
          all || Boolean(nextType?.uses_interest),
          'interest_rate_type_id',
          fields.interest_rate_type_id
        )
        term(
          'compounding_frequency_id',
          all || Boolean(nextType?.uses_interest),
          'compounding_frequency_id',
          fields.compounding_frequency_id
        )
        term(
          'day_count_convention_id',
          all || Boolean(nextType?.uses_interest),
          'day_count_convention_id',
          fields.day_count_convention_id
        )
        term('maturity_date', all || Boolean(nextType?.uses_maturity), 'maturity_date', fields.maturity_date)
        term(
          'maturity_months',
          all || Boolean(nextType?.uses_maturity && nextType?.is_convertible),
          'maturity_months',
          fields.maturity_months
        )
        term(
          'valuation_cap_type_id',
          all || Boolean(nextType?.is_convertible),
          'valuation_cap_type_id',
          fields.valuation_cap_type_id
        )
        term('valuation_cap', all || Boolean(nextType?.is_convertible), 'valuation_cap', fields.valuation_cap)
        term('valuation_floor', all || Boolean(nextType?.is_convertible), 'valuation_floor', fields.valuation_floor)
        term(
          'conversion_discount',
          all || Boolean(nextType?.is_convertible),
          'conversion_discount',
          percentToDecimal(fields.conversion_discount)
        )
        term('strike_price', all || Boolean(nextType?.uses_strike), 'strike_price', fields.strike_price)
        put('fd_common_equivalent', 'fd_common_equivalent', fields.fd_common_equivalent)
        // notes deliberately never sent — this card does not edit them.

        if (Object.keys(payload).length > 0) {
          const token = getAccessToken(store.getState().auth)
          const res = await saveSecurity(token, securityId, payload as SaveSecurityPayload)
          if (!res.success) {
            showErrorToast(res.error || 'Failed to save the security')
            return
          }
          securityWritten = true
        }
      }
      setEditing(false)
      if (securityWritten) await onSecuritySaved()
    } finally {
      setSaving(false)
    }
  }
  saveRef.current = save

  const startEdit = (): void => {
    baselineRef.current = fields
    setEditing(true)
  }

  const cancel = (): void => {
    setFields(fieldsFromInstrument(instrument))
    setKind(instrument.round_instruments__kind as Kind)
    setEditing(false)
  }

  // Clicking away from an open block saves it. AntD renders select / date
  // popups in portals outside the block, so a click inside one of those is
  // still a click "inside" as far as this is concerned.
  useEffect(() => {
    if (!editing) return undefined
    const onPointerDown = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null
      if (!target || !blockRef.current) return
      if (blockRef.current.contains(target)) return
      if (target.closest('.ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown, .ant-modal-root, .ant-message')) {
        return
      }
      // mousedown fires BEFORE the focused input blurs, so blur it and let
      // React flush that state before reading the draft — otherwise the value
      // still being typed when the operator clicks away is lost.
      ;(document.activeElement as HTMLElement | null)?.blur?.()
      setTimeout(() => void saveRef.current(), 0)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [editing])

  const confirmDelete = (): void => {
    modal.confirm({
      title: 'Remove this security from the round?',
      content:
        'The link to the round is removed. The security itself is kept — it stays available to transactions and other rounds.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: onRemove
    })
  }

  return (
    <div ref={blockRef} className='border border-gray-100 rounded p-2 mb-2'>
      {modalContextHolder}
      <div className='flex items-center gap-2 mb-1'>
        <Select
          size='small'
          className='w-32 flex-none'
          value={kind}
          disabled={!editing}
          options={kindOptions}
          onChange={(v) => setKind(v as Kind)}
        />
        <Tag color={KIND_COLORS[instrument.round_instruments__kind] || 'default'} className='m-0'>
          {instrument.cfg_security_types__label || 'No type'}
        </Tag>
        {securityId && (
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-xs'
            onClick={() => openSecurity(securityId, fields.name || 'Security')}
          >
            Open security
          </button>
        )}
        <span className='ml-auto flex items-center gap-1'>
          {editing ? (
            <>
              <Button size='small' type='text' disabled={saving} onClick={cancel}>
                Cancel
              </Button>
              <Button size='small' type='primary' loading={saving} onClick={save}>
                Save
              </Button>
            </>
          ) : (
            <Button size='small' icon={<EditOutlined />} onClick={startEdit}>
              Edit
            </Button>
          )}
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'change',
                  label: 'Change',
                  icon: <SwapOutlined />,
                  onClick: () => setRelinking(true)
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: confirmDelete
                }
              ]
            }}
          >
            <Button size='small' type='text' icon={<MoreOutlined />} />
          </Dropdown>
        </span>
      </div>

      {/* The link picker only shows when there is nothing linked yet, or when
          Change is picked from the menu. */}
      {!securityId || relinking ? (
        <Row label='Security'>
          <span className='flex items-center gap-1'>
            <Select
              size='small'
              className='flex-1 min-w-0'
              showSearch
              placeholder='Select security…'
              value={securityId ?? undefined}
              options={securityOptions}
              filterOption={(input, option) =>
                `${option?.label ?? ''} ${(option as { typeLabel?: string })?.typeLabel ?? ''}`
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              onChange={async (v) => {
                await onRelink(v ?? null)
                setRelinking(false)
              }}
            />
            {/* Change picked by mistake — back out without re-pointing it. */}
            {securityId && relinking && (
              <Button size='small' type='text' onClick={() => setRelinking(false)}>
                Cancel
              </Button>
            )}
          </span>
        </Row>
      ) : null}

      {securityId ? (
        <>
          <Row label='Name'>
            <Input
              size='small'
              value={fields.name}
              disabled={!editing}
              onChange={(e) => setDraft({ name: e.target.value })}
            />
          </Row>
          <Row label='Security type'>
            <Select
              size='small'
              className='w-full'
              value={fields.type_id ?? undefined}
              disabled={!editing}
              options={securityTypes.map((t: any) => ({ value: t.id, label: t.label }))}
              onChange={(v) => {
                // Type drives the valuation-type and FD-equivalent defaults
                // (same chain as the "+ New security" modal).
                const t = securityTypes.find((x: any) => x.id === v)
                setDraft({
                  type_id: v,
                  valuation_type_id: t?.default_valuation_type_id ?? fields.valuation_type_id,
                  fd_common_equivalent:
                    t?.default_fd_common_equivalent ?? fields.fd_common_equivalent
                })
              }}
            />
          </Row>
          <Row label='Valuation type'>
            <Select
              size='small'
              className='w-full'
              value={fields.valuation_type_id ?? undefined}
              disabled={!editing}
              options={valuationTypes.map((v: any) => ({ value: v.id, label: v.label }))}
              onChange={(v) => setDraft({ valuation_type_id: v })}
            />
          </Row>
          <Row label='Currency'>
            <Select
              size='small'
              className='w-full'
              showSearch
              optionFilterProp='label'
              value={fields.currency_id ?? undefined}
              disabled={!editing}
              options={currencies.map((c: any) => ({ value: c.id, label: c.code }))}
              onChange={(v) => setDraft({ currency_id: v })}
            />
          </Row>
          {showIssuePrice && (
            <Row label='Issue price'>
              <InputNumber
                size='small'
                className='w-full'
                isCurrency
                currencySymbol={ccySymbol}
                disabled={!editing}
                value={fields.issue_price ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ issue_price: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showNominal && (
            <Row label='Nominal value'>
              <InputNumber
                size='small'
                className='w-full'
                isCurrency
                currencySymbol={ccySymbol}
                disabled={!editing}
                value={fields.nominal_value ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ nominal_value: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showCapType && (
            <Row label='Valuation cap type'>
              <Select
                size='small'
                className='w-full'
                allowClear
                disabled={!editing}
                value={fields.valuation_cap_type_id ?? undefined}
                options={valuationCapTypes.map((v: any) => ({ value: v.id, label: v.label }))}
                onChange={(v) => setDraft({ valuation_cap_type_id: v ?? null })}
              />
            </Row>
          )}
          {showCap && (
            <Row label='Valuation cap'>
              <InputNumber
                size='small'
                className='w-full'
                isCurrency
                currencySymbol={ccySymbol}
                disabled={!editing}
                value={fields.valuation_cap ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ valuation_cap: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showFloor && (
            <Row label='Valuation floor'>
              <InputNumber
                size='small'
                className='w-full'
                isCurrency
                currencySymbol={ccySymbol}
                disabled={!editing}
                value={fields.valuation_floor ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ valuation_floor: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showDiscount && (
            <Row label='Conversion discount %'>
              <InputNumber
                size='small'
                className='w-full'
                min={0}
                max={100}
                disabled={!editing}
                value={fields.conversion_discount ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ conversion_discount: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showInterest && (
            <>
              <Row label='Interest rate %'>
                <InputNumber
                  size='small'
                  className='w-full'
                  min={0}
                  disabled={!editing}
                  value={fields.interest_rate ?? undefined}
                  onChange={(v: number | null) =>
                  setDraft({ interest_rate: v == null ? null : Number(v) })
                }
                />
              </Row>
              <Row label='Interest rate type'>
                <Select
                  size='small'
                  className='w-full'
                  allowClear
                  disabled={!editing}
                  value={fields.interest_rate_type_id ?? undefined}
                  options={interestRateTypes.map((r: any) => ({ value: r.id, label: r.label }))}
                  onChange={(v) => setDraft({ interest_rate_type_id: v ?? null })}
                />
              </Row>
              <Row label='Day count'>
                <Select
                  size='small'
                  className='w-full'
                  allowClear
                  disabled={!editing}
                  value={fields.day_count_convention_id ?? undefined}
                  options={dayCountConventions.map((d: any) => ({ value: d.id, label: d.label }))}
                  onChange={(v) => setDraft({ day_count_convention_id: v ?? null })}
                />
              </Row>
            </>
          )}
          {showCompounding && (
            <Row label='Compounding'>
              <Select
                size='small'
                className='w-full'
                allowClear
                disabled={!editing}
                value={fields.compounding_frequency_id ?? undefined}
                options={compoundingFrequencies.filter((c: any) => !c.once_off).map((c: any) => ({ value: c.id, label: c.label }))}
                onChange={(v) => setDraft({ compounding_frequency_id: v ?? null })}
              />
            </Row>
          )}
          {showMaturity && (
            <Row label='Maturity date'>
              <DatePicker
                size='small'
                className='w-full'
                format={DATE_FORMAT}
                disabled={!editing}
                value={fields.maturity_date ? dayjs(fields.maturity_date) : null}
                onChange={(v) => setDraft({ maturity_date: v ? v.format('YYYY-MM-DD') : null })}
              />
            </Row>
          )}
          {showMaturityMonths && (
            <Row label='Maturity (months)'>
              <InputNumber
                size='small'
                className='w-full'
                min={0}
                precision={0}
                disabled={!editing}
                value={fields.maturity_months ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ maturity_months: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          {showStrike && (
            <Row label='Strike price'>
              <InputNumber
                size='small'
                className='w-full'
                isCurrency
                currencySymbol={ccySymbol}
                disabled={!editing}
                value={fields.strike_price ?? undefined}
                onChange={(v: number | null) =>
                  setDraft({ strike_price: v == null ? null : Number(v) })
                }
              />
            </Row>
          )}
          <Row label='FD common equivalent'>
            <InputNumber
              size='small'
              className='w-full'
              disabled={!editing}
              value={fields.fd_common_equivalent ?? undefined}
              onChange={(v: number | null) =>
                  setDraft({ fd_common_equivalent: v == null ? null : Number(v) })
                }
            />
          </Row>
        </>
      ) : (
        <div className='text-xs text-gray-400 py-1'>
          Pick the security this row records, or create a new one with the button above.
        </div>
      )}
    </div>
  )
}

export default function RoundSecuritiesCard({
  context,
  companyId,
  companyName,
  onSaveInstrument,
  onRemoveInstrument,
  onSecuritySaved
}: RoundSecuritiesCardProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const currencies = useSelector(
    (state: { globalConfigs?: { currencies?: Array<{ id: string; code: string }> } }) =>
      state.globalConfigs?.currencies || []
  )

  const [addKind, setAddKind] = useState<Kind | null>(null)
  const [addSecurityId, setAddSecurityId] = useState<string | null>(null)
  const [newSecurityOpen, setNewSecurityOpen] = useState(false)

  const round = context?.round
  const instruments = useMemo(() => context?.round_instruments || [], [context])
  const companySecurities = useMemo(() => context?.company_securities || [], [context])

  // New securities default to the deal's currency (the modal falls back to the
  // base currency when the deal has none).
  const dealCurrencyId = currencies.find((c) => c.code === context?.currencies__code)?.id ?? null

  const securityOptions = useMemo(
    () =>
      companySecurities.map((s) => ({
        value: s.securities__id,
        label: s.securities__name || 'Security',
        typeLabel: s.cfg_security_types__label || ''
      })),
    [companySecurities]
  )

  const primaryRowId = instruments.find((i) => i.round_instruments__kind === 'primary')
    ?.round_instruments__id

  const kindOptionsFor = (
    rowId: string | null
  ): { value: Kind; label: string; disabled?: boolean }[] =>
    KIND_OPTIONS.map((o) =>
      o.value === 'primary' && primaryRowId != null && primaryRowId !== rowId
        ? { ...o, disabled: true }
        : o
    )

  const saveLink = async (
    instrumentId: string | null,
    kind: Kind,
    securityId: string | null
  ): Promise<boolean> => {
    if (!securityId) {
      showErrorToast('Pick the security (or create a new one)')
      return false
    }
    const ok = await onSaveInstrument(instrumentId, { kind, security_id: securityId })
    if (!ok) showErrorToast('Failed to save the round security')
    return ok
  }

  const startAdd = (): void => {
    setAddKind(primaryRowId ? 'secondary' : 'primary')
    setAddSecurityId(null)
  }

  const cancelAdd = (): void => {
    setAddKind(null)
    setAddSecurityId(null)
  }

  return (
    <FormSection
      title='Round Securities'
      titleColor='purple'
      scrollable={false}
      className='h-full'
      headerActions={
        round ? (
          <Button
            size='small'
            type='primary'
            icon={<PlusOutlined />}
            disabled={addKind != null}
            onClick={startAdd}
          >
            Security
          </Button>
        ) : undefined
      }
    >
      {!round ? (
        <div className='text-sm text-gray-400 py-2'>
          Link a funding round to record the securities it issues.
        </div>
      ) : (
        <>
          {instruments.map((instrument) => (
            <SecurityBlock
              key={instrument.round_instruments__id}
              instrument={instrument}
              kindOptions={kindOptionsFor(instrument.round_instruments__id)}
              securityOptions={securityOptions}
              openSecurity={(id, name) => openFormInTab('securities', id, name)}
              onKindChange={async (kind) => {
                await saveLink(
                  instrument.round_instruments__id,
                  kind,
                  instrument.securities__id ?? null
                )
              }}
              onRelink={async (securityId) => {
                await saveLink(
                  instrument.round_instruments__id,
                  instrument.round_instruments__kind as Kind,
                  securityId
                )
              }}
              onRemove={async () => {
                const ok = await onRemoveInstrument(instrument.round_instruments__id)
                if (!ok) showErrorToast('Failed to remove the round security')
              }}
              onSecuritySaved={onSecuritySaved}
            />
          ))}

          {/* Adding is a two-part choice — the category, and which security
              (an existing one or a brand-new one). Its terms then edit inline
              in the block it becomes. */}
          {addKind != null && (
            <div className='border border-blue-100 bg-blue-50/40 rounded p-2 mb-2'>
              <Row label='Category'>
                <Select
                  size='small'
                  className='w-full'
                  value={addKind}
                  options={kindOptionsFor(null)}
                  onChange={(v) => setAddKind(v as Kind)}
                />
              </Row>
              <Row label='Security'>
                <span className='flex items-center gap-2'>
                  <Select
                    size='small'
                    className='flex-1 min-w-0'
                    showSearch
                    placeholder='Select security…'
                    value={addSecurityId ?? undefined}
                    options={securityOptions}
                    filterOption={(input, option) =>
                      `${option?.label ?? ''} ${(option as { typeLabel?: string })?.typeLabel ?? ''}`
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    onChange={(v) => setAddSecurityId(v ?? null)}
                  />
                  <Tooltip title={`Create a new security issued by ${companyName || 'the company'}`}>
                    <Button
                      size='small'
                      icon={<PlusOutlined />}
                      onClick={() => setNewSecurityOpen(true)}
                    />
                  </Tooltip>
                </span>
              </Row>
              <div className='flex justify-end gap-2 mt-1'>
                <Button size='small' type='text' onClick={cancelAdd}>
                  Cancel
                </Button>
                <Button
                  size='small'
                  type='primary'
                  onClick={async () => {
                    const ok = await saveLink(null, addKind, addSecurityId)
                    if (ok) cancelAdd()
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          )}

          {instruments.length === 0 && addKind == null && (
            <div className='text-xs text-gray-400'>
              No securities on this round yet — add the primary instrument with the button above.
            </div>
          )}
          <div className='text-xs text-gray-400 mt-1'>
            Primary = the instrument this round issues · Secondary = existing paper purchased ·
            Converting = instruments converting into the round. The terms are the security&apos;s
            own and save as you enter them.
          </div>
        </>
      )}

      {/* Create the security and link it in one step — a deal execution is
          usually the first moment the security exists. */}
      <AddSecurityModal
        open={newSecurityOpen}
        companyId={companyId ?? null}
        companyName={companyName}
        defaultCurrencyId={dealCurrencyId}
        onCancel={() => setNewSecurityOpen(false)}
        onCreated={async (securityId) => {
          setNewSecurityOpen(false)
          const ok = await saveLink(null, addKind ?? 'primary', securityId)
          if (ok) cancelAdd()
        }}
      />
    </FormSection>
  )
}
